import React from 'react';
import { Info } from 'lucide-react';
import { Components } from 'react-markdown';
import { slugify } from '../utils/slugify';

const INFO_MARKER = /^\[!INFO\]\s*/i;

/** Strips a leading `[!INFO]` marker from the first text child, if present. */
function stripInfoMarker(children: React.ReactNode): { isInfo: boolean; content: React.ReactNode } {
  const nodes = React.Children.toArray(children);
  const [first] = nodes;

  if (typeof first === 'string' && INFO_MARKER.test(first)) {
    nodes[0] = first.replace(INFO_MARKER, '');
    return { isInfo: true, content: nodes };
  }

  return { isInfo: false, content: children };
}

function headingRenderer(level: 2 | 3 | 4) {
  const Tag = `h${level}` as 'h2' | 'h3' | 'h4';
  const sizeClass =
    level === 2 ? 'text-xl font-semibold mt-10' : level === 3 ? 'text-lg font-semibold mt-8' : 'text-base font-semibold mt-6';

  const HeadingComponent = ({ children, ...props }: React.ComponentPropsWithoutRef<typeof Tag>) => {
    const text = String(children);
    return (
      <Tag id={slugify(text)} className={`scroll-mt-24 ${sizeClass} text-foreground`} {...props}>
        {children}
      </Tag>
    );
  };
  HeadingComponent.displayName = `DocsHeading${level}`;
  return HeadingComponent;
}

export const docsMarkdownComponentsMap: Partial<Components> = {
  h2: headingRenderer(2),
  h3: headingRenderer(3),
  h4: headingRenderer(4),
  p: ({ children }) => {
    const { isInfo, content } = stripInfoMarker(children);
    if (isInfo) {
      return (
        <p className="text-sm leading-relaxed text-foreground/90 mt-4 flex items-start gap-2">
          <Info className="mt-0.5 size-4 shrink-0 text-link-accent" />
          <span>{content}</span>
        </p>
      );
    }
    return <p className="text-sm leading-relaxed text-foreground/90 mt-4">{children}</p>;
  },
  ul: ({ children }) => <ul className="list-disc pl-5 text-sm text-foreground/90 mt-4 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 text-sm text-foreground/90 mt-4 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  a: ({ href = '', children }) => {
    const isInternal = href.startsWith('/') || href.startsWith('#');
    return (
      <a
        href={href}
        className="text-link-accent underline underline-offset-2 hover:opacity-80"
        {...(!isInternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {children}
      </a>
    );
  },
  blockquote: ({ children }) => (
    <div className="mt-4 flex items-start gap-2 rounded-lg border border-border bg-muted/50 px-4 py-3 text-sm text-foreground/90">
      {children}
    </div>
  ),
  code: ({ className, children }) => (
    <code className={`rounded bg-muted px-1.5 py-0.5 text-xs font-mono ${className ?? ''}`}>{children}</code>
  ),
  pre: ({ children }) => (
    <pre className="mt-4 overflow-x-auto rounded-lg bg-muted p-4 text-xs font-mono">{children}</pre>
  ),
  img: ({ src, alt }) => (
    <img src={src} alt={alt} loading="lazy" className="mt-2 block h-auto w-full rounded-lg border border-border" />
  ),
  // Wrapped so wide tables scroll inside the page instead of pushing it sideways on phones.
  table: ({ children }) => (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border-b border-border bg-muted/50 px-3 py-2 text-left font-semibold text-foreground">{children}</th>
  ),
  td: ({ children }) => <td className="border-b border-border px-3 py-2 align-top text-foreground/90">{children}</td>,
};
